/**
 * True when a leaderboard/picks `name` (the player's email) belongs to the
 * signed-in user. Case-insensitive: auth emails and stored names have not
 * always agreed on case.
 *
 * @param {string|null} name - Email stored on the leaderboard or picks doc
 * @param {Object|null} currentUser - Firebase auth user
 * @returns {boolean}
 */
export function isCurrentUser(name, currentUser) {
  const email = currentUser?.email;
  if (!name || !email) return false;
  return name.toLowerCase() === email.toLowerCase();
}

/**
 * Points are multiples of 0.25 summed in floating point; round for display.
 */
export function formatPoints(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return String(Math.round(n * 100) / 100);
}

/**
 * Standard competition rank ("1, 2, 2, 4") of each row by totalPoints, keyed
 * by email, as { rank, tied }.
 */
export function ranksByTotal(rows) {
  const totals = rows.map((r) => Number(r.totalPoints) || 0);
  const ranks = {};
  rows.forEach((row, i) => {
    const mine = totals[i];
    ranks[row.email] = {
      rank: 1 + totals.filter((t) => t > mine).length,
      tied: totals.filter((t) => t === mine).length > 1,
    };
  });
  return ranks;
}

/** Same ranks as labels, with a "T-" prefix on shared places ("T-2nd"). */
export function rankLabelsByTotal(rows) {
  const labels = {};
  for (const [email, { rank, tied }] of Object.entries(ranksByTotal(rows))) {
    labels[email] = `${tied ? 'T-' : ''}${ordinal(rank)}`;
  }
  return labels;
}

/** 1 -> "1st", 2 -> "2nd", 11 -> "11th", 22 -> "22nd". */
export function ordinal(n) {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  return `${n}${{ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th'}`;
}
